export function renderBuildSingleStepFormComponentSourceClientModuleTemplate({
  lucideImport,
  rhfImport,
  pascal,
  actionImportPath,
  zodFields,
  fieldErrorHelper,
  defaults,
  fieldArraySetup,
  watchSetup,
  successHandler,
  successMessage,
  fieldJsx,
  submitText
}: {
  lucideImport: string
  rhfImport: string
  pascal: string
  actionImportPath: string
  zodFields: string
  fieldErrorHelper: string
  defaults: string
  fieldArraySetup: string
  watchSetup: string
  successHandler: string
  successMessage: string
  fieldJsx: string
  submitText: string
}): string {
  return `'use client'

import * as React from 'react'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'${lucideImport}
${rhfImport}
import { z } from 'zod/v3'
import { create${pascal}Submission } from '@admin/actions/${actionImportPath}'

const formSchema = z.object({
${zodFields}
})

type FormValues = z.infer<typeof formSchema>
${fieldErrorHelper}

export function ${pascal}Form() {
  const [submitted, setSubmitted] = React.useState(false)
  const [submitting, startSubmitTransition] = React.useTransition()

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    defaultValues: {
${defaults}
    },
  })
${fieldArraySetup}${watchSetup}
  function onSubmit(values: FormValues) {
    startSubmitTransition(async () => {
      try {
        const result = await create${pascal}Submission(values)
        if (result.success) {
          ${successHandler}
        } else {
          form.setError('root', { message: result.error || 'Something went wrong' })
        }
      } catch {
        form.setError('root', { message: 'Something went wrong. Please try again.' })
      }
    })
  }

  if (submitted) {
    return (
      <div className="rounded-sm border p-6 text-center">
        <h3 className="text-lg font-semibold">Thank you!</h3>
        <p className="mt-2 text-muted-foreground">${successMessage}</p>
      </div>
    )
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
${fieldJsx}

      {form.formState.errors.root && (
        <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        aria-busy={submitting}
        className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
      >
        {submitting ? 'Submitting…' : ${submitText}}
      </button>
    </form>
  )
}
`
}
